/**
 * Firestore side of the case lookup: everything the booking system holds about
 * one email address.
 *
 * Two things about the data shape drive the design here:
 *
 *   1. Email addresses on `clients` are stored with inconsistent casing, and
 *      Firestore queries are case-sensitive. A definitive answer therefore needs
 *      the collection read once and matched in memory (see buildClientIndex).
 *   2. One address can have more than one client account, which is why the merge
 *      clients tool exists. Everything downstream fans out over every account
 *      found, never just the first.
 */

import firebase from "firebase"
import { db } from "../../../utils/firebase"
import { resolveCustomerPrice, toISO } from "./values"

/**
 * Staff names, loaded once per run so enquiries and bookings can name the agent
 * who handled them rather than showing a bare uid.
 */
export const fetchStaff = async () => {
	const staff = new Map()

	try {
		const snapshot = await db.collection("users").get()

		snapshot.forEach((doc) => {
			const data = doc.data() || {}
			const name = [data.first_name, data.last_name].filter(Boolean).join(" ")
			staff.set(doc.id, name || data.email || doc.id)
		})
	} catch (error) {
		// Non-fatal: the report falls back to showing uids
	}

	return staff
}

/**
 * Shape a client document for the report.
 */
const shapeClient = (id, data) => ({
	id,
	email: data.email || "",
	firstName: data.first_name || "",
	lastName: data.last_name || "",
	phone: data.phone || "",
	created: toISO(data.created),
	address: [data.address_line_one, data.address_line_two, data.town, data.county, data.postcode].filter(Boolean).join(", "),
	hasPortalLogin: Boolean(data.uid || data.auth_uid),
})

/**
 * Read every client document once and index them by lowercase email.
 *
 * Firestore queries are case-sensitive and client emails are stored with
 * inconsistent casing, so no set of equality queries can promise it found every
 * account for an address. Reading the collection once and matching in memory
 * can, which is what a report going into a legal file needs. Paged by document
 * id so the browser never holds one enormous response, and only the fields the
 * report uses are retained.
 */
export const buildClientIndex = async (onProgress) => {
	const index = new Map()
	const pageSize = 2000
	const byID = firebase.firestore.FieldPath.documentId()

	let cursor = null
	let scanned = 0

	for (;;) {
		let query = db.collection("clients").orderBy(byID).limit(pageSize)

		if (cursor) query = query.startAfter(cursor)

		const snapshot = await query.get()

		if (snapshot.empty) break

		snapshot.forEach((doc) => {
			const data = doc.data() || {}
			const key = String(data.email || "").toLowerCase().trim()

			if (!key) return

			const existing = index.get(key) || []
			existing.push(shapeClient(doc.id, data))
			index.set(key, existing)
		})

		scanned += snapshot.size
		cursor = snapshot.docs[snapshot.docs.length - 1]

		if (onProgress) onProgress({ scanned })

		if (snapshot.size < pageSize) break
	}

	return { index, scanned }
}

/**
 * Client accounts for one address.
 *
 * Uses the full index when one was built. Without it, falls back to exact
 * queries on the likely casings, which is quicker but cannot rule out an account
 * stored with unexpected capitalisation - a caveat the report then carries.
 */
export const findClientAccounts = async (email, clientIndex) => {
	const lower = email.toLowerCase().trim()

	if (clientIndex) return clientIndex.get(lower) || []

	const capitalised = lower.charAt(0).toUpperCase() + lower.slice(1)
	const variants = [...new Set([email, lower, capitalised, email.toUpperCase()])]

	const snapshots = await Promise.all(variants.map((variant) => db.collection("clients").where("email", "==", variant).limit(50).get().catch(() => null)))

	const accounts = new Map()

	for (const snapshot of snapshots) {
		snapshot?.forEach((doc) => {
			const data = doc.data() || {}

			// Only a genuine case-insensitive match counts
			if (String(data.email || "").toLowerCase().trim() !== lower) return

			accounts.set(doc.id, shapeClient(doc.id, data))
		})
	}

	return [...accounts.values()]
}

/**
 * Enquiries raised by any of this address's client accounts, live and deleted.
 *
 * Enquiry documents hold no email of their own, so they are only reachable
 * through a client account. An address with no account therefore has no
 * enquiries by definition, which the report states explicitly.
 */
export const findEnquiries = async (clientIDs, staff) => {
	const enquiries = []

	for (const clientID of clientIDs) {
		const [live, removed] = await Promise.all([
			db.collection("enquiries").where("client", "==", clientID).get().catch(() => null),
			db.collection("enquiries_removed").where("enquiry.client", "==", clientID).get().catch(() => null),
		])

		live?.forEach((doc) => {
			const data = doc.data() || {}

			enquiries.push({
				id: doc.id,
				clientID,
				state: "Live",
				created: toISO(data.created),
				travelDate: toISO(data.date_from),
				location: data.location || "",
				groupSize: data.group_size ?? null,
				nights: data.nights ?? null,
				rounds: data.rounds ?? null,
				quoted: data.quoted ?? null,
				status: data.status || "",
				inboundChannel: data.inbound_channel || "",
				site: data.site || "",
				agent: staff.get(data.agent) || data.agent || "",
				utmSource: data.tracking?.utm_source || "",
				utmCampaign: data.tracking?.utm_campaign || "",
				utmMedium: data.tracking?.utm_medium || "",
				notes: data.notes || "",
			})
		})

		removed?.forEach((doc) => {
			const data = doc.data() || {}
			const enquiry = data.enquiry || {}

			enquiries.push({
				id: doc.id,
				clientID,
				state: "Deleted",
				created: toISO(enquiry.created),
				travelDate: toISO(enquiry.date_from),
				location: enquiry.location || "",
				groupSize: enquiry.group_size ?? null,
				nights: enquiry.nights ?? null,
				rounds: enquiry.rounds ?? null,
				quoted: enquiry.quoted ?? null,
				status: enquiry.status || "",
				inboundChannel: enquiry.inbound_channel || "",
				site: enquiry.site || "",
				agent: staff.get(enquiry.agent) || enquiry.agent || "",
				utmSource: enquiry.tracking?.utm_source || "",
				utmCampaign: enquiry.tracking?.utm_campaign || "",
				utmMedium: enquiry.tracking?.utm_medium || "",
				notes: enquiry.notes || "",
				deletedAt: toISO(data.removed || data.deleted_at),
				deletedBy: staff.get(data.removed_by) || data.removed_by || "",
			})
		})
	}

	enquiries.sort((a, b) => String(a.created).localeCompare(String(b.created)))

	return enquiries
}

/**
 * Bookings held against any of this address's client accounts, plus the payments
 * recorded on each one.
 */
export const findBookings = async (clientIDs, staff) => {
	const bookings = []

	for (const clientID of clientIDs) {
		const snapshot = await db.collection("bookings").where("client", "==", clientID).get().catch(() => null)

		if (!snapshot) continue

		for (const doc of snapshot.docs) {
			const data = doc.data() || {}
			const cost = resolveCustomerPrice(data)

			// Payments taken against the booking - the counterweight to the customer
			// price when the report states what was actually received
			let paymentTotal = 0
			let paymentCount = 0

			const payments = await db.collection(`bookings/${doc.id}/payments`).get().catch(() => null)

			payments?.forEach((payment) => {
				const amount = Number(payment.data()?.amount)

				if (!Number.isNaN(amount)) paymentTotal += amount

				paymentCount += 1
			})

			bookings.push({
				id: doc.id,
				clientID,
				reference: data.reference || "",
				state: data.removed ? "Removed" : "Live",
				confirmed: Boolean(data.confirmed),
				created: toISO(data.created),
				booked: toISO(data.booked),
				travelDate: toISO(data.check_in || data.date_from),
				location: data.enquiry?.location || "",
				golfers: data.golfers ?? null,
				nonGolfers: data.non_golfers ?? null,
				customerPrice: cost.value,
				customerPriceSource: cost.source,
				paidByClient: Number(data.paid_by_client) || 0,
				paymentsRecorded: paymentTotal,
				paymentCount,
				agent: staff.get(data.agent) || data.agent || "",
				eventTag: data.event_tag || "",
			})
		}
	}

	bookings.sort((a, b) => String(a.created).localeCompare(String(b.created)))

	return bookings
}

/**
 * Quotes sent to this address: internal quote documents and the durable public
 * quote projections behind any /quote/:token link.
 */
export const findQuotes = async (clientIDs) => {
	const quotes = []

	for (const clientID of clientIDs) {
		const [internal, published] = await Promise.all([
			db.collection("quotes").where("client", "==", clientID).get().catch(() => null),
			db.collection("public_quotes").where("client", "==", clientID).get().catch(() => null),
		])

		internal?.forEach((doc) => {
			const data = doc.data() || {}

			quotes.push({
				id: doc.id,
				clientID,
				kind: "Internal quote",
				created: toISO(data.created),
				status: data.status || "",
				location: data.location || data.enquiry?.location || "",
				value: data.total ?? data.quoted ?? null,
			})
		})

		published?.forEach((doc) => {
			const data = doc.data() || {}

			quotes.push({
				id: doc.id,
				clientID,
				kind: "Online quote",
				created: toISO(data.created || data.created_at),
				status: data.status || "",
				location: data.location || data.summary?.location || "",
				value: data.total ?? data.price_total ?? null,
				viewedAt: toISO(data.first_viewed_at || data.viewed_at),
			})
		})
	}

	quotes.sort((a, b) => String(a.created).localeCompare(String(b.created)))

	return quotes
}

/**
 * Online bookings taken directly on the website against these accounts.
 */
export const findDirectBookings = async (clientIDs) => {
	const directBookings = []

	for (const clientID of clientIDs) {
		const snapshot = await db.collection("direct_bookings").where("client", "==", clientID).get().catch(() => null)

		snapshot?.forEach((doc) => {
			const data = doc.data() || {}

			directBookings.push({
				id: doc.id,
				clientID,
				created: toISO(data.created),
				travelDate: toISO(data.check_in || data.date_from),
				location: data.location || data.enquiry?.location || "",
				total: Number(data.total ?? data.margins?.customer_price ?? 0) || 0,
				paidByClient: Number(data.paid_by_client) || 0,
				paidStatus: data.paid_status || "",
			})
		})
	}

	return directBookings
}

/**
 * Gift vouchers bought using this address.
 */
export const findGiftVouchers = async (email) => {
	const vouchers = []
	const lower = email.toLowerCase().trim()

	const snapshot = await db.collection("gift_vouchers").where("email", "==", lower).get().catch(() => null)

	snapshot?.forEach((doc) => {
		const data = doc.data() || {}

		vouchers.push({
			id: doc.id,
			created: toISO(data.created),
			value: data.value ?? null,
			type: data.voucher_type || data.type || "",
			status: data.status || "",
			firstName: data.first_name || "",
			lastName: data.last_name || "",
		})
	})

	return vouchers
}

/**
 * Email correspondence held in the monitored-mailbox archive.
 *
 * Two passes: anything linked to one of the client accounts, which covers both
 * directions, and any inbound message whose sender address matches, which covers
 * mail that arrived before a client account existed. Outbound mail that was never
 * linked to an account cannot be matched, because recipients are stored as an
 * array of objects that Firestore cannot query into - the report says so.
 *
 * Reading `communications` needs an admin or communications-admin account. If the
 * rules refuse the query we record that rather than failing the address, so the
 * report never implies "no correspondence" when it means "could not look".
 */
export const findCorrespondence = async (clientIDs, email) => {
	const messages = new Map()
	const lower = email.toLowerCase().trim()

	let accessible = true

	const collect = (snapshot) => {
		snapshot?.forEach((doc) => {
			const data = doc.data() || {}

			messages.set(doc.id, {
				id: doc.id,
				direction: data.direction || "",
				type: data.type || "",
				occurredAt: toISO(data.occurred_at),
				subject: data.subject || "",
				from: data.from?.address || "",
				to: (data.to || []).map((participant) => participant?.address).filter(Boolean).join(", "),
				classification: typeof data.classification === "string" ? data.classification : data.classification?.label || "",
				hasUnsubscribeLink: Boolean(data.body_has_unsubscribe),
			})
		})
	}

	const queries = [
		...clientIDs.map((clientID) => db.collection("communications").where("client_id", "==", clientID).limit(500).get()),
		db.collection("communications").where("from.address", "==", lower).limit(500).get(),
	]

	const results = await Promise.all(
		queries.map((query) =>
			query.catch((error) => {
				if (String(error?.code || error?.message || "").includes("permission-denied")) accessible = false

				return null
			})
		)
	)

	results.forEach(collect)

	const sorted = [...messages.values()].sort((a, b) => String(a.occurredAt).localeCompare(String(b.occurredAt)))

	return { messages: sorted, accessible }
}
